'use strict';

/**
 * @fileoverview A map whose entries are fixed at construction — and which cannot be changed by ANY
 * route (MUTATION-INSTRUMENT-001 F-TOCONTAIN-2). The Map twin of `ReadOnlySet`; see that module for
 * why `Object.freeze(new Map(...))` promises nothing: `set` / `delete` / `clear` write an internal
 * slot the freeze never reaches, and `Map.prototype.set.call` reaches a subclass instance's slot.
 *
 * NOT a `Map`: the entries live in a private `#entries` Map that no outside code can name, only the
 * read half of the Map contract is exposed (`get`, `has`, `size`, iteration, `forEach`, `keys`,
 * `values`, `entries`), and every intrinsic Map mutator called on it meets an incompatible receiver
 * and throws. The instance, the prototype and the class are frozen, and `forEach` hands its callback
 * this object — never the backing Map.
 *
 * @module @puffin-core/read-only/readOnlyMap
 */

class ReadOnlyMap {
  /** The fixed entries. Private: reachable by no code outside this class body. */
  #entries;

  /**
   * @param {Iterable<Array<*>>} [entries] - `[key, value]` pairs; copied, so later changes to the
   *   source are not seen.
   */
  constructor(entries) {
    this.#entries = new Map(entries);
    Object.freeze(this);
  }

  /**
   * @param {*} key
   * @returns {*} The value stored under `key`, or `undefined`.
   */
  get(key) {
    return this.#entries.get(key);
  }

  /**
   * @param {*} key
   * @returns {boolean} Whether `key` has an entry.
   */
  has(key) {
    return this.#entries.has(key);
  }

  /** @returns {number} The entry count. */
  get size() {
    return this.#entries.size;
  }

  /**
   * Calls `callback(value, key, this)` per entry, in insertion order — the Map contract, with the
   * ReadOnlyMap (never the private backing Map) as the third argument.
   *
   * @param {(value: *, key: *, map: ReadOnlyMap) => void} callback
   * @param {*} [thisArg]
   * @throws {TypeError} When `callback` is not a function, as `Map#forEach` does.
   */
  forEach(callback, thisArg) {
    if (typeof callback !== 'function') {
      throw new TypeError('ReadOnlyMap#forEach: callback must be a function');
    }
    for (const [key, value] of this.#entries) {
      callback.call(thisArg, value, key, this);
    }
  }

  /** @returns {Iterator<*>} The keys. */
  keys() {
    return this.#entries.keys();
  }

  /** @returns {Iterator<*>} The values. */
  values() {
    return this.#entries.values();
  }

  /** @returns {Iterator<Array<*>>} `[key, value]` pairs. */
  entries() {
    return this.#entries.entries();
  }

  /** @returns {Iterator<Array<*>>} `[key, value]` pairs, as `Map#[Symbol.iterator]`. */
  [Symbol.iterator]() {
    return this.#entries.entries();
  }

  /** @returns {string} `'ReadOnlyMap'`, for `Object.prototype.toString`. */
  get [Symbol.toStringTag]() {
    return 'ReadOnlyMap';
  }
}

Object.freeze(ReadOnlyMap.prototype);
Object.freeze(ReadOnlyMap);

module.exports = { ReadOnlyMap };
